import { InformationCircleIcon, StarIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import {
  ActionIcon,
  Autocomplete,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  rem,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import React, { useEffect, useState } from "react";
import { useColors } from "../../hooks/useColors";
import { ChatService } from "../../services/chatService";

export interface TechnologyWithFavorite {
  id: number;
  name: string;
  isFavorite: boolean;
}

interface TechnologySelectorProps<T extends TechnologyWithFavorite> {
  label: string;
  labelTooltip?: string;
  placeholder?: string;
  value: T[];
  onChange: (technologies: T[]) => void;
  availableTechnologies: { id: number; value: string; label: string }[];
  disabled?: boolean;
  loading?: boolean;
  enableSuggestions?: boolean;
  required?: boolean;
}

export function TechnologySelector<T extends TechnologyWithFavorite>({
  label,
  labelTooltip,
  placeholder = "Type to search and add technologies...",
  value,
  onChange,
  availableTechnologies,
  disabled = false,
  loading = false,
  enableSuggestions = false,
  required = false,
}: TechnologySelectorProps<T>) {
  const [searchValue, setSearchValue] = useState("");
  const [suggestedTechnologies, setSuggestedTechnologies] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const colors = useColors();
  const chatService = ChatService.getInstance();

  // Clear search input whenever technologies change
  useEffect(() => {
    setSearchValue("");
  }, [value.length]);

  // Fetch technology suggestions when a new technology is added
  const fetchSuggestions = async (technologyName: string) => {
    if (!enableSuggestions) return;

    setLoadingSuggestions(true);
    try {
      const suggestions = await chatService.suggestSimilarSkills(technologyName);

      // Filter suggestions to only include technologies that exist in our dictionary
      // and are not already selected
      const filteredSuggestions = suggestions.filter((suggestion) => {
        const existsInDictionary = availableTechnologies.some(
          (tech) => tech.label.toLowerCase() === suggestion.toLowerCase()
        );
        const notAlreadySelected = !value.some(
          (tech) => tech.name.toLowerCase() === suggestion.toLowerCase()
        );
        return existsInDictionary && notAlreadySelected;
      });

      setSuggestedTechnologies(filteredSuggestions);
    } catch (error) {
      console.error("Error fetching technology suggestions:", error);
      setSuggestedTechnologies([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleAddTechnology = (technologyName: string) => {
    if (!technologyName.trim()) return;

    // Find the technology in available options to get its ID
    const foundTech = availableTechnologies.find(
      (tech) =>
        tech.label.toLowerCase() === technologyName.toLowerCase() ||
        tech.value.toLowerCase() === technologyName.toLowerCase()
    );

    if (!foundTech) {
      console.warn(
        `Technology "${technologyName}" not found in available technologies`
      );
      setSearchValue(""); // Clear input even if technology not found
      return;
    }

    // Check if technology already exists
    const exists = value.some((tech) => tech.id === foundTech.id);
    if (exists) {
      setSearchValue(""); // Clear input if technology already exists
      return;
    }

    // Add new technology (not favorite by default)
    const newTechnology = {
      id: foundTech.id,
      name: foundTech.label,
      isFavorite: false,
    } as T;

    onChange([...value, newTechnology]);
    setSearchValue(""); // Clear input after adding technology

    // Fetch suggestions for the newly added technology
    if (enableSuggestions) {
      fetchSuggestions(foundTech.label);
    }
  };

  const handleRemoveTechnology = (technologyId: number) => {
    onChange(value.filter((tech) => tech.id !== technologyId));
    // Clear suggestions when removing a technology
    setSuggestedTechnologies([]);
  };

  const handleToggleFavorite = (technologyId: number) => {
    onChange(
      value.map((tech) =>
        tech.id === technologyId
          ? ({ ...tech, isFavorite: !tech.isFavorite } as T)
          : tech
      )
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && searchValue.trim()) {
      event.preventDefault();
      handleAddTechnology(searchValue.trim());
    }
  };

  // Filter available technologies to exclude already selected ones
  const filteredOptions = availableTechnologies.filter(
    (tech) => !value.some((selected) => selected.id === tech.id)
  );

  // Handle adding suggested technology
  const handleAddSuggestedTechnology = (suggestionName: string) => {
    handleAddTechnology(suggestionName);
    // Remove the suggestion from the list after adding it
    setSuggestedTechnologies(prev => prev.filter(s => s !== suggestionName));
  };

  return (
    <Stack gap="xs">
      <Group gap={4} align="center">
        <Text size="sm" fw={500}>
          {label}
          {required && <Text component="span" c="red" ml={4} aria-hidden>*</Text>}
          {loading && <Loader size="xs" ml="xs" />}
        </Text>
        {labelTooltip && (
          <Tooltip label={labelTooltip} position="top" multiline maw={260} withArrow>
            <ActionIcon variant="subtle" color="gray" size="xs" tabIndex={-1}>
              <InformationCircleIcon style={{ width: rem(14) }} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>

      <Autocomplete
        placeholder={placeholder}
        value={searchValue}
        onChange={setSearchValue}
        onOptionSubmit={handleAddTechnology}
        onKeyDown={handleKeyDown}
        data={filteredOptions.map((tech) => ({
          value: tech.value,
          label: tech.label,
        }))}
        disabled={disabled || loading}
        limit={10}
      />

      {value.length > 0 && (
        <Box>
          <Group gap="xs">
            {value.map((tech) => (
              <Badge
                key={tech.id}
                size="lg"
                variant={tech.isFavorite ? "filled" : "light"}
                color={
                  tech.isFavorite
                    ? colors.getBrandColor("complimentaryBlue")
                    : colors.getBrandColor("complimentaryBlue")
                }
                rightSection={
                  <Group gap={4} wrap="nowrap">
                    <ActionIcon
                      size="xs"
                      variant="transparent"
                      onClick={() => handleToggleFavorite(tech.id)}
                      title={
                        tech.isFavorite
                          ? "Remove from favorites"
                          : "Mark as favorite"
                      }
                    >
                      {tech.isFavorite ? (
                        <StarIconSolid width={12} height={12} />
                      ) : (
                        <StarIcon width={12} height={12} />
                      )}
                    </ActionIcon>
                    <ActionIcon
                      size="xs"
                      variant="transparent"
                      onClick={() => handleRemoveTechnology(tech.id)}
                      title="Remove technology"
                    >
                      <XMarkIcon width={12} height={12} />
                    </ActionIcon>
                  </Group>
                }
              >
                {tech.name}
              </Badge>
            ))}
          </Group>

          {value.some((tech) => tech.isFavorite) && (
            <Text size="xs" c="dimmed" mt="xs">
              ⭐ Starred technologies indicate your key/preferred technologies
              for this experience
            </Text>
          )}
        </Box>
      )}

      {/* Technology Suggestions */}
      {enableSuggestions && suggestedTechnologies.length > 0 && (
        <Box>
          <Group align="center" gap="xs" mb="xs">
            <Text size="sm" fw={500} c="dimmed">
              Suggested similar technologies:
            </Text>
            {loadingSuggestions && <Loader size="xs" />}
          </Group>
          <Group gap="xs">
            {suggestedTechnologies.map((suggestion) => (
              <Button
                key={suggestion}
                variant="outline"
                size="xs"
                onClick={() => handleAddSuggestedTechnology(suggestion)}
                disabled={disabled || loading}
              >
                + {suggestion}
              </Button>
            ))}
          </Group>
          <Button
            variant="subtle"
            size="xs"
            onClick={() => setSuggestedTechnologies([])}
            mt="xs"
          >
            Hide suggestions
          </Button>
        </Box>
      )}
    </Stack>
  );
}
